import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import * as XLSX from "xlsx";
import { config } from "../../config";

interface IPreviewCSVFileProps {
  filePath: string;
}

export const PreviewCSVFile: React.FC<IPreviewCSVFileProps> = ({
  filePath,
}) => {
  const [data, setData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    const parseCSV = async () => {
      try {
        setLoading(true);
        setError(null);

        let arrayBuffer: ArrayBuffer;

        // CASE 1: Base64 file
        if (filePath.startsWith("data:")) {
          const base64 = filePath.split(",")[1];
          const binary = atob(base64);
          const bytes = new Uint8Array(binary.length);

          for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
          }

          arrayBuffer = bytes.buffer;
        }

        // CASE 2: Remote file
        else {
          const fileUrl = `${config.assets.s3BaseUrl}/${filePath}`;
          const response = await fetch(fileUrl);

          if (!response.ok) {
            throw new Error(`Failed to fetch CSV file`);
          }

          arrayBuffer = await response.arrayBuffer();
        }

        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData: string[][] = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        if (jsonData.length > 0) {
          setHeaders(jsonData[0].map((h) => String(h)));
          setData(jsonData.slice(1).map((row) => row.map((c) => String(c))));
        }
      } catch (err: any) {
        console.error("CSV parse error:", err);
        setError(err.message || "Failed to load CSV file");
      } finally {
        setLoading(false);
      }
    };

    if (filePath) parseCSV();
  }, [filePath]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
        <Typography ml={2}>Loading CSV file...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Typography color="textSecondary">No data found in CSV file</Typography>
      </Box>
    );
  }

  const paginatedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box sx={{ width: "100%", minHeight: "90vh" }}>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "80vh" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    minWidth: 50,
                  }}
                >
                  #
                </TableCell>
                {headers.map((header, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      fontWeight: 600,
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      minWidth: 120,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header || `Column ${index + 1}`}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: "action.hover",
                    },
                    "&:hover": {
                      backgroundColor: "action.selected",
                    },
                  }}
                >
                  <TableCell sx={{ color: "text.secondary", fontWeight: 500 }}>
                    {page * rowsPerPage + rowIndex + 1}
                  </TableCell>
                  {headers.map((_, cellIndex) => (
                    <TableCell
                      key={cellIndex}
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={row[cellIndex] || ""}
                    >
                      {row[cellIndex] || "-"}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};
